import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { CategoryData } from "@/hooks/use-dashboard-analytics";
import { useValuesVisibility } from "@/hooks/use-values-visibility";
import * as LucideIcons from "lucide-react";

interface TopCategoriesWidgetProps {
  data: CategoryData[];
  isLoading?: boolean;
}

export function TopCategoriesWidget({ data, isLoading }: TopCategoriesWidgetProps) {
  const { mask } = useValuesVisibility();

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Top Categorias</CardTitle>
          <CardDescription>Maiores gastos do mês</CardDescription>
        </CardHeader>
        <CardContent className="h-[200px] flex items-center justify-center">
          <div className="animate-pulse text-muted-foreground">Carregando...</div>
        </CardContent>
      </Card>
    );
  }

  const topCategories = data.slice(0, 5);
  const maxAmount = topCategories.length > 0 ? topCategories[0].amount : 0;

  const formatCurrency = (value: number) =>
    mask(new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value));

  const getIcon = (iconName: string) => {
    const Icon = (LucideIcons as any)[iconName] || LucideIcons.CircleDot;
    return Icon;
  };

  if (topCategories.length === 0) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">Top Categorias</CardTitle>
          <CardDescription>Maiores gastos do mês</CardDescription>
        </CardHeader>
        <CardContent className="h-[200px] flex items-center justify-center">
          <p className="text-muted-foreground text-sm">Nenhum gasto registrado</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-lg">Top Categorias</CardTitle>
        <CardDescription>Maiores gastos do mês</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {topCategories.map((category, index) => {
          const Icon = getIcon(category.icon);
          const progressValue = maxAmount > 0 ? (category.amount / maxAmount) * 100 : 0;

          return (
            <div key={category.id} className="space-y-1.5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div
                    className="p-1.5 rounded-md"
                    style={{ backgroundColor: `${category.color}20` }}
                  >
                    <Icon
                      className="h-3.5 w-3.5"
                      style={{ color: category.color }}
                    />
                  </div>
                  <span className="text-sm font-medium truncate max-w-[100px]">
                    {category.name}
                  </span>
                </div>
                <div className="text-right">
                  <span className="text-sm font-medium">
                    {formatCurrency(category.amount)}
                  </span>
                  <span className="text-xs text-muted-foreground ml-1">
                    ({category.percentage.toFixed(0)}%)
                  </span>
                </div>
              </div>
              <div className="relative h-1.5 w-full bg-muted rounded-full overflow-hidden">
                <div
                  className="absolute inset-y-0 left-0 rounded-full transition-all duration-500"
                  style={{
                    width: `${progressValue}%`,
                    backgroundColor: category.color,
                  }}
                />
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
